import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRightIcon } from '@/components/ui/Icons';
import type { ReaderTag } from '@/api/client';
import { ContextMenuSubmenu } from '@/components/ui/ContextMenu/ContextMenuSubmenu';
import { ReaderCatalogReaderTagsToggleList } from '@/components/ReadersTable/ReaderCatalogReaderTagsToggleList';
import clsx from 'clsx';

const SUBMENU_CLOSE_MS = 160;

/** Выше родительского сабменю «Теги чтеца» (1100). */
const GROUP_TAGS_SUBMENU_Z = 1120;

export interface ReaderCatalogReaderTagGroupCascadeRowProps {
  groupLabel: string;
  tags: ReaderTag[];
  assignedIds: Set<string>;
  disabled?: boolean;
  onToggleTag: (tagId: string, on: boolean) => void;
}

export const ReaderCatalogReaderTagGroupCascadeRow = memo(function ReaderCatalogReaderTagGroupCascadeRow({
  groupLabel,
  tags,
  assignedIds,
  disabled,
  onToggleTag,
}: ReaderCatalogReaderTagGroupCascadeRowProps) {
  const [submenuAnchor, setSubmenuAnchor] = useState<HTMLElement | null>(null);
  const timerRef = useRef<number | null>(null);

  const selectedCount = useMemo(
    () => tags.reduce((n, t) => n + (assignedIds.has(t.id) ? 1 : 0), 0),
    [tags, assignedIds]
  );

  const scheduleSubmenuClose = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      setSubmenuAnchor(null);
      timerRef.current = null;
    }, SUBMENU_CLOSE_MS);
  }, []);

  const cancelSubmenuClose = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  return (
    <li
      className="context-menu__list-item"
      onMouseEnter={(e) => {
        if (disabled) return;
        cancelSubmenuClose();
        setSubmenuAnchor(e.currentTarget);
      }}
      onMouseLeave={scheduleSubmenuClose}
    >
      <button
        type="button"
        className="context-menu__item context-menu__action_with-sub reader-catalog-tag-group-cascade__row"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={Boolean(submenuAnchor)}
        aria-label={`${groupLabel}, выбрано тегов: ${selectedCount}`}
        role="menuitem"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (disabled) return;
          cancelSubmenuClose();
          const row = e.currentTarget.closest('.context-menu__list-item');
          if (!(row instanceof HTMLElement)) return;
          setSubmenuAnchor((prev) => (prev === row ? null : row));
        }}
      >
        <span className="context-menu__iconCell reader-catalog-tag-group-cascade__icon-cell" aria-hidden>
          <span
            className={clsx(
              'reader-catalog-tag-group-cascade__badge',
              selectedCount > 0 && 'reader-catalog-tag-group-cascade__badge_active'
            )}
          >
            {selectedCount}
          </span>
        </span>
        <span className="context-menu__item-text">{groupLabel}</span>
        <span className="reader-catalog-tag-group-cascade__trail">
          <ChevronRightIcon size={16} className="context-menu__action-chevron" aria-hidden />
        </span>
      </button>
      {submenuAnchor ? (
        <ContextMenuSubmenu
          anchorEl={submenuAnchor}
          onMouseEnter={cancelSubmenuClose}
          onMouseLeave={scheduleSubmenuClose}
          zIndex={GROUP_TAGS_SUBMENU_Z}
        >
          <ReaderCatalogReaderTagsToggleList
            tags={tags}
            assignedIds={assignedIds}
            disabled={disabled}
            onToggleTag={onToggleTag}
          />
        </ContextMenuSubmenu>
      ) : null}
    </li>
  );
});
